import { useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import type { Document } from "@/types/document";
import { useToast } from "@/hooks/use-toast";
import { Button } from "./ui/button";
import { FormatSelector, type DownloadFormat } from "./document-download/FormatSelector";
import { Loader } from "./ui/loader";
import { API_ENDPOINTS } from "@/config/api";

interface DocumentDownloadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  document: Document | null;
}

export function DocumentDownloadDialog({
  isOpen,
  onClose,
  document: documentData,
}: DocumentDownloadDialogProps) {
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState<DownloadFormat>("pdf");
  


  const handleDownload = async () => {
    if (!documentData || !documentData.file_path) return;
    
    try {
      setIsDownloading(true);
      console.log('Starting download process');

      const requestBody = {
        file_path: documentData.file_path,
        format: downloadFormat,
        placeholder_values: documentData.placeholder_values || {},
        document_name: documentData.filename
      };

      console.log('Making request with body:', requestBody);

      const response = await fetch(API_ENDPOINTS.GENERATE_DOCUMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json, application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        },
        mode: 'cors',
        body: JSON.stringify(requestBody)
      });

      // Check for network errors
      if (!response) {
        throw new Error('Network error occurred');
      }

      // Check for non-200 responses
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server response error:', response.status, errorText);
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      if (!blob) {
        throw new Error('No data received from server');
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Use the actual document name for the download
      a.download = `${documentData.filename}.${downloadFormat}`;
      
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Success",
        description: `Document downloaded as ${downloadFormat.toUpperCase()}`,
      });
      onClose();
    } catch (error) {
      console.error('Error downloading document:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to download document",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const isDownloadEnabled = downloadFormat;

  return (
    <AlertDialog 
      open={isOpen} 
      onOpenChange={(open) => {
        if (!open && !isDownloading) {
          setDownloadFormat("pdf");
          onClose();
        }
      }}
    >
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Download Document</AlertDialogTitle>
          <AlertDialogDescription>
            Choose your download preferences below.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-6 py-4">
          <FormatSelector
            downloadFormat={downloadFormat}
            onFormatChange={setDownloadFormat}
          />
        </div>

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isDownloading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!isDownloadEnabled || isDownloading}
          >
            {isDownloading ? (
              <div className="flex items-center gap-2">
                <Loader size={16} />
                <span>Downloading...</span>
              </div>
            ) : (
              "Download"
            )}
          </Button>
        </AlertDialogFooter>
        
        {isDownloading && (
          <div className="mt-4 flex justify-center">
            <Loader text="Preparing your document..." />
          </div>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
